import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import CategoryNav from '@/components/feature/CategoryNav';
import { videos, categories } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [filteredVideos, setFilteredVideos] = useState(videos);
  const [sortMode, setSortMode] = useState<'default' | 'hot' | 'new'>('default');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    let result = [...videos];

    if (slug && slug !== 'all') {
      result = result.filter((v) => v.category === slug);
    }

    const sort = searchParams.get('sort');
    if (sort === 'hot') {
      setSortMode('hot');
      result.sort((a, b) => b.views - a.views);
    } else if (sort === 'new') {
      setSortMode('new');
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else {
      setSortMode('default');
    }

    setFilteredVideos(result);
  }, [slug, searchParams]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 果冻传媒`,
    description: `果冻传媒${currentCategory.name}频道，海量${currentCategory.name}高清视频免费在线观看`,
    url: `https://www.guodongmedia.com/category/${slug}`,
    isPartOf: { '@type': 'WebSite', name: '果冻传媒' },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
          </nav>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredVideos.length} 部作品
            </p>
          </div>

          {/* Category Nav */}
          <div className="mb-8">
            <CategoryNav activeSlug={slug || 'all'} />
          </div>

          {/* Sort Toggle */}
          <div className="flex items-center gap-3 mb-6">
            {[
              { mode: 'default' as const, label: '推荐' },
              { mode: 'hot' as const, label: '最热' },
              { mode: 'new' as const, label: '最新' },
            ].map((item) => (
              <Link
                key={item.mode}
                to={item.mode === 'default' ? `/category/${slug || 'all'}` : `/category/${slug || 'all'}?sort=${item.mode}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  sortMode === item.mode
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的内容</p>
            </div>
          )}

          {/* Empty state fillers */}
          {filteredVideos.length > 0 && filteredVideos.length < 4 && (
            <div className="text-center py-8 text-sm text-foreground-400">
              更多内容正在制作中，敬请期待...
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}